import React, {useEffect, useState} from 'react';
import {Button, Input, Table, message, Space, Modal} from "antd";
import {SearchOutlined, SaveOutlined, DeleteOutlined} from "@ant-design/icons";
import api from "../../api";

const { TextArea } = Input;

export default function AdminTranslations() {
    const [translations, setTranslations] = useState([]);
    const [filteredTranslations, setFilteredTranslations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingKeys, setEditingKeys] = useState(new Set());

    useEffect(() => {
        loadTranslations();
    }, []);

    const loadTranslations = () => {
        setLoading(true);
        api.getAllTranslationsForAdmin()
            .then((data) => {
                setTranslations(data);
                setFilteredTranslations(data);
                setLoading(false);
            })
            .catch(() => {
                message.error('Failed to load translations');
                setLoading(false);
            });
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (value.trim() === '') {
            setFilteredTranslations(translations);
        } else {
            const filtered = translations.filter(item =>
                item.key.toLowerCase().includes(value.toLowerCase()) ||
                item.nl.toLowerCase().includes(value.toLowerCase()) ||
                item.en.toLowerCase().includes(value.toLowerCase()) ||
                item.fr.toLowerCase().includes(value.toLowerCase()) ||
                item.de.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredTranslations(filtered);
        }
    };

    const handleTranslationChange = (key, language, value) => {
        const updatedTranslations = translations.map(item => {
            if (item.key === key) {
                return { ...item, [language]: value };
            }
            return item;
        });
        setTranslations(updatedTranslations);
        
        // Also update filtered translations
        const updatedFiltered = filteredTranslations.map(item => {
            if (item.key === key) {
                return { ...item, [language]: value };
            }
            return item;
        });
        setFilteredTranslations(updatedFiltered);

        // Mark as editing
        setEditingKeys(prev => new Set([...prev, key]));
    };

    const handleSave = (key) => {
        const translationItem = translations.find(item => item.key === key);
        if (!translationItem) return;

        const translationData = {
            nl: translationItem.nl,
            en: translationItem.en,
            fr: translationItem.fr,
            de: translationItem.de
        };

        api.updateTranslation(key, translationData)
            .then(() => {
                message.success(`Translations updated for "${key}"`);
                setEditingKeys(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(key);
                    return newSet;
                });
            })
            .catch(() => {
                message.error(`Failed to update translations for "${key}"`);
            });
    };

    const handleDelete = (key) => {
        Modal.confirm({
            title: 'Delete Translation',
            content: `Are you sure you want to delete all translations for "${key}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                return api.deleteTranslation(key)
                    .then(() => {
                        message.success(`Translation "${key}" deleted successfully`);
                        // Remove from local state
                        setTranslations(prev => prev.filter(item => item.key !== key));
                        setFilteredTranslations(prev => prev.filter(item => item.key !== key));
                        // Remove from editing keys if present
                        setEditingKeys(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(key);
                            return newSet;
                        });
                    })
                    .catch(() => {
                        message.error(`Failed to delete translation "${key}"`);
                    });
            }
        });
    };

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 250,
            fixed: 'left',
            render: (key) => (
                <div style={{ wordBreak: 'break-word' }}>
                    <strong>{key}</strong>
                </div>
            ),
        },
        {
            title: 'Dutch (NL)',
            dataIndex: 'nl',
            key: 'nl',
            width: 200,
            render: (value, record) => (
                <TextArea
                    value={value}
                    onChange={(e) => handleTranslationChange(record.key, 'nl', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                />
            ),
        },
        {
            title: 'English (EN)',
            dataIndex: 'en',
            key: 'en',
            width: 200,
            render: (value, record) => (
                <TextArea
                    value={value}
                    onChange={(e) => handleTranslationChange(record.key, 'en', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                />
            ),
        },
        {
            title: 'French (FR)',
            dataIndex: 'fr',
            key: 'fr',
            width: 200,
            render: (value, record) => (
                <TextArea
                    value={value}
                    onChange={(e) => handleTranslationChange(record.key, 'fr', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                />
            ),
        },
        {
            title: 'German (DE)',
            dataIndex: 'de',
            key: 'de',
            width: 200,
            render: (value, record) => (
                <TextArea
                    value={value}
                    onChange={(e) => handleTranslationChange(record.key, 'de', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type={editingKeys.has(record.key) ? 'primary' : 'default'}
                        icon={<SaveOutlined />}
                        onClick={() => handleSave(record.key)}
                        disabled={!editingKeys.has(record.key)}
                    >
                        Save
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.key)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <h1>Translation Management</h1>
            <p>Manage translations for Dutch (NL), English (EN), French (FR), and German (DE)</p>
            
            <div style={{ marginBottom: 16 }}>
                <Input
                    placeholder="Search translations by key or value..."
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ maxWidth: 400 }}
                    allowClear
                />
                <Button 
                    onClick={loadTranslations} 
                    style={{ marginLeft: 8 }}
                >
                    Refresh
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={filteredTranslations}
                loading={loading}
                rowKey="key"
                scroll={{ x: 1300 }}
                pagination={{
                    pageSize: 50,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                        `${range[0]}-${range[1]} of ${total} translations`,
                }}
                size="small"
            />

            {editingKeys.size > 0 && (
                <div style={{ 
                    position: 'fixed', 
                    bottom: 20, 
                    right: 20, 
                    background: '#fff', 
                    padding: '8px 16px', 
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    {editingKeys.size} unsaved change{editingKeys.size > 1 ? 's' : ''}
                </div>
            )}
        </div>
    );
}